import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PAYPAL_ME_URL, PRO_PRICE_LABEL } from '../../config/billing';

/**
 * Global upgrade modal for the paywall bridge (BAB-71).
 *
 * Mounted once inside the Router. Listens for the `babylon:upgrade-required`
 * event that the axios interceptor dispatches on any 402 (a Free user hitting a
 * Pro endpoint), and shows a single non-blocking modal that routes to /pricing.
 * "Show the gold, gate the action" — the user already saw the feature; this
 * explains why it's locked and where to unlock it, instead of a raw error.
 */
interface UpgradeDetail {
  message?: string;
  required_tier?: string;
  upgrade_url?: string;
}

const UpgradeModal: React.FC = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<UpgradeDetail>({});

  useEffect(() => {
    const handler = (e: Event) => {
      const d = (e as CustomEvent).detail as UpgradeDetail | undefined;
      setDetail(d || {});
      setOpen(true);
    };
    window.addEventListener('babylon:upgrade-required', handler);
    return () => window.removeEventListener('babylon:upgrade-required', handler);
  }, []);

  const close = useCallback(() => setOpen(false), []);

  const goToPricing = useCallback(() => {
    setOpen(false);
    navigate(detail.upgrade_url || '/pricing');
  }, [navigate, detail.upgrade_url]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={close}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#1e1e1e',
          border: '1px solid #333333',
          borderRadius: 14,
          maxWidth: 420,
          width: '100%',
          padding: '28px 26px',
          boxShadow: '0 20px 50px rgba(0,0,0,0.55)',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.5, color: '#60a5fa', textTransform: 'uppercase' }}>
          Pro feature
        </div>
        <h2 style={{ margin: '10px 0 8px', fontSize: 22, color: '#ffffff' }}>
          Unlock this with Babylon Pro
        </h2>
        <p style={{ margin: '0 0 22px', color: '#94a3b8', fontSize: 15, lineHeight: 1.5 }}>
          {detail.message ||
            'Real-Yield, roll-vs-assign, and your full performance dashboard are part of Pro — about 0.1% of a $250k portfolio, vs the 0.35%+ an income ETF charges to cap your upside.'}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button
            onClick={goToPricing}
            style={{
              background: '#2962ff',
              color: '#fff',
              border: 'none',
              borderRadius: 9,
              padding: '12px 16px',
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            See plans
          </button>
          <button
            onClick={close}
            style={{
              background: 'transparent',
              color: '#94a3b8',
              border: 'none',
              padding: '8px',
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            Maybe later
          </button>
        </div>
        <p style={{ margin: '20px 0 0', paddingTop: 16, borderTop: '1px solid #333333', fontSize: 12.5, color: '#94a3b8', lineHeight: 1.5 }}>
          Trial ended?{' '}
          <a href={PAYPAL_ME_URL} target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa', fontWeight: 600 }}>
            Pay {PRO_PRICE_LABEL} via PayPal
          </a>{' '}
          and we'll switch Pro back on within a day.
        </p>
      </div>
    </div>
  );
};

export default UpgradeModal;
